import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectStaffCaseCreation } from '@brands/store/selectors/staffCaseCreation';
import { clearLinkedCase } from '@brands/store/slices/linkedCaseSlice';
import { setStaffIntakeInProgress } from '@brands/store/slices/staffCaseCreationSlice';
import { isUnifiedStaffCaseIntakeRole } from '@brands/Utils/reviewProfileBasePath';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@mui/material';
import React, { useCallback } from 'react';

import styles from './styles.module.scss';

type AbandonIntakeProcessModalType = {
  setDisplayAbandonIntakeProcessModal: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirmExit?: () => void;
};

const AbandonIntakeProcessModal: React.FC<AbandonIntakeProcessModalType> = ({
  setDisplayAbandonIntakeProcessModal,
  onConfirmExit,
}) => {
  const roleNavigate = useRoleNavigate();
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const { isStaffIntake } = useAppSelector(selectStaffCaseCreation);
  const ClearSessionStorage = (): void => {
    sessionStorage.removeItem('interestedInStopping');
    sessionStorage.removeItem('medicalHistoryData');
    sessionStorage.removeItem('heightAndWeight');
    sessionStorage.removeItem('allergies');
    sessionStorage.removeItem('medications');
    sessionStorage.removeItem('dentalHistory');
    sessionStorage.removeItem('substances');
    sessionStorage.removeItem('conditionsTreated');
    sessionStorage.removeItem('underCare');
    sessionStorage.removeItem('concerns');
  };
  const goToDashboard = useCallback(() => {
    ClearSessionStorage();
    // This is the single shared "abandon intake" exit point for every step
    // of the wizard — without this, closing out of a linked case's
    // completion mid-way leaves linkedCaseId/linkedCasePatientId/
    // linkedCaseNeedsPayment/previousClinicalData sitting in Redux (not
    // persist-blacklisted, so it survives a reload too) to be picked up by
    // whatever the patient does next.
    dispatch(clearLinkedCase());

    if (onConfirmExit) {
      if (isStaffIntake && isUnifiedStaffCaseIntakeRole(userInfo.role.name)) {
        dispatch(setStaffIntakeInProgress(false));
      }

      onConfirmExit();
      return;
    }

    if (isStaffIntake && isUnifiedStaffCaseIntakeRole(userInfo.role.name)) {
      dispatch(setStaffIntakeInProgress(false));
    }

    roleNavigate('/dashboard');
  }, [userInfo.role.name, roleNavigate, isStaffIntake, dispatch, onConfirmExit]);

  return (
    <div className={styles.modalBackground} onClick={() => setDisplayAbandonIntakeProcessModal(false)}>
      <div
        className={`${styles.modalContainer} ${styles.unavailableTimeSlot} fs-unmask`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalCloseIcon}>
          <button type="button" onClick={() => setDisplayAbandonIntakeProcessModal(false)} aria-label="Close modal">
            <CloseIcon />
          </button>
        </div>
        <div className={styles.body}>
          <p>Are you sure you want to leave?</p>
          <span>
            If you close now, your unsaved information will be lost. Do you want to continue exiting or complete the
            process?
          </span>
          <Button
            data-testid="leave-intake-yes"
            variant="contained"
            color="error"
            className={styles.exitIntake}
            onClick={() => goToDashboard()}
          >
            Yes, Exit to Dashboard
          </Button>
          <Button
            variant="text"
            className={styles.keepIntake}
            onClick={() => setDisplayAbandonIntakeProcessModal(false)}
            data-testid="leave-intake-no"
          >
            Return to Intake
          </Button>
        </div>
      </div>
    </div>
  );
};
export default AbandonIntakeProcessModal;
